import React from "react";
import {
  FiBox,
  FiCamera,
  FiCheckCircle,
  FiClock,
  FiDroplet,
  FiFileText,
  FiGrid,
  FiHexagon,
  FiLayers,
  FiMaximize2,
  FiMinimize2,
  FiPrinter,
  FiScissors,
  FiTarget,
  FiUpload,
  FiXCircle,
  FiZap,
  FiAlertTriangle,
  FiCircle,
  FiSquare,
  FiLink,
  FiSettings,
  FiFlag,
} from "react-icons/fi";
import { FaTooth } from "react-icons/fa6";
import {
  GiAnvil,
  GiCrystalShine,
  GiCutDiamond,
  GiIncisors,
  GiJawbone,
  GiPerfumeBottle,
  GiStoneBlock,
  GiTooth,
  GiToothbrush,
} from "react-icons/gi";
import { TbBraces, TbDental, TbStack2 } from "react-icons/tb";
import { RiToothFill, RiToothLine } from "react-icons/ri";
import { PiToothFill, PiTooth } from "react-icons/pi";
import { BiPalette, BiCube, BiLayer } from "react-icons/bi";
import { HiOutlineSparkles } from "react-icons/hi2";
import { MdOutlineScience } from "react-icons/md";

const iconClass = "h-6 w-6";
const optClass = "h-5 w-5";

/** Masked PNG → solid black (light) / solid white (dark + selected) via .cu-service-icon-mono */
function monoPngIcon(src: string): React.ReactNode {
  return (
    <span
      role="img"
      aria-hidden
      className={`${iconClass} cu-service-icon-mono inline-block`}
      style={{
        WebkitMaskImage: `url('${src}')`,
        maskImage: `url('${src}')`,
        WebkitMaskSize: "contain",
        maskSize: "contain",
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        maskPosition: "center",
      }}
    />
  );
}

/** Unique icon per service */
export const SERVICE_ICONS: Record<string, React.ReactNode> = {
  "Crown & Bridge": <GiTooth className={iconClass} />,
  Implant: monoPngIcon("/Form/implant.png"),
  "Smile Design / Mock-up": <GiIncisors className={iconClass} />,
  "No-prep Veneers": <RiToothFill className={iconClass} />,
  "LDP Complete denture": monoPngIcon("/Form/Complete%20Denture.png"),
  "LDP Partial denture": monoPngIcon("/Form/Partial%20Denture.png"),
  "RPD Framework": monoPngIcon("/Form/RPD%20Framework.png"),
  "Surgical Guide": <FiTarget className={iconClass} />,
  "Nightguard / Bite splint": monoPngIcon("/Form/Nightguard.png"),
  "Digital model": monoPngIcon("/Form/DigitalModel.png"),
  "Other treatments": <GiJawbone className={iconClass} />,
  "Upload filled order": <FiUpload className={iconClass} />,
};

/** Varied pool so long material lists don't all share one icon */
const MATERIAL_POOL: React.ReactNode[] = [
  <GiCutDiamond className={optClass} key="d" />,
  <GiCrystalShine className={optClass} key="c" />,
  <GiStoneBlock className={optClass} key="s" />,
  <GiAnvil className={optClass} key="a" />,
  <MdOutlineScience className={optClass} key="sci" />,
  <FiDroplet className={optClass} key="drop" />,
  <FiPrinter className={optClass} key="print" />,
  <GiPerfumeBottle className={optClass} key="wax" />,
  <FiHexagon className={optClass} key="hex" />,
  <BiLayer className={optClass} key="layer" />,
  <HiOutlineSparkles className={optClass} key="spark" />,
  <FiCircle className={optClass} key="cir" />,
];

function hashIcon(value: string): React.ReactNode {
  let h = 0;
  for (let i = 0; i < value.length; i++) h = (h * 31 + value.charCodeAt(i)) | 0;
  const idx = Math.abs(h) % MATERIAL_POOL.length;
  return MATERIAL_POOL[idx];
}

/** Material-specific icons when we can classify the type */
export function getMaterialIcon(value: string): React.ReactNode {
  const k = value.toLowerCase().trim();

  // Clear material *types* get a semantic icon
  if (k === "zirconia" || k === "zircad prime" || k === "zircad prime esthetic")
    return <GiCutDiamond className={optClass} />;
  if (k === "emax" || k === "milled emax" || k === "pressed e.max")
    return <GiCrystalShine className={optClass} />;
  if (/^metal/.test(k)) return <GiAnvil className={optClass} />;
  if (k === "composite" || k === "milled composite")
    return <MdOutlineScience className={optClass} />;
  if (/acrylic|pmma/.test(k)) return <FiDroplet className={optClass} />;
  if (/peek|hpp/.test(k)) return <FiHexagon className={optClass} />;
  if (k === "wax") return <GiPerfumeBottle className={optClass} />;

  // Brand / product names: unique hashed icon so long lists don't repeat
  return hashIcon(value);
}

/** Best-effort related icon — returns null if nothing specific (avoids repeats) */
export function getOptionIcon(
  value: string,
  label: string
): React.ReactNode | null {
  const key = `${value} ${label}`.toLowerCase().trim();
  const bare = value.toLowerCase().trim();

  if (bare === "yes") return <FiCheckCircle className={optClass} />;
  if (bare === "no") return <FiXCircle className={optClass} />;

  if (/high priority|rush/.test(key)) return <FiZap className={optClass} />;
  if (/medium priority|regular/.test(key)) return <FiClock className={optClass} />;
  if (/low priority/.test(key)) return <FiAlertTriangle className={optClass} />;

  // Prosthesis types — each unique
  if (/\binlay\b/.test(key)) return <RiToothLine className={optClass} />;
  if (/onlay|overlay/.test(key)) return <FaTooth className={optClass} />;
  if (/\bcrown\b/.test(key) && !/bridge/.test(key))
    return <GiTooth className={optClass} />;
  if (/maryland/.test(key)) return <TbStack2 className={optClass} />;
  if (/\bbridge\b|pontic/.test(key)) return <GiIncisors className={optClass} />;
  if (/veneer/.test(key)) return <RiToothFill className={optClass} />;

  // Design types
  if (/full contour/.test(key)) return <FiMaximize2 className={optClass} />;
  if (/facial cutback/.test(key)) return <FiMinimize2 className={optClass} />;
  if (/coping/.test(key)) return <FiLayers className={optClass} />;

  // Materials
  if (
    /zircon|emax|composite|acrylic|pmma|metal|print|katana|peek|wax|argen|celtra|coltene|sagemax|vita|zircad|3m|brecam|luxor|livento|whitepeaks|dental direct|gc cerasmart|milled|pressed|bio hpp|enamic/i.test(
      key
    )
  )
    return getMaterialIcon(value);

  // Implant restorations — distinct
  if (/custom abutment \+ crown|abutment \+ crown/.test(key))
    return <TbDental className={optClass} />;
  if (/abutment only/.test(key)) return <FiHexagon className={optClass} />;
  if (/screw retained/.test(key)) return <FiLink className={optClass} />;
  if (/engaging/.test(key) && /non/.test(key))
    return <FiCircle className={optClass} />;
  if (/\bengaging\b/.test(key)) return <FiCheckCircle className={optClass} />;
  if (/\bmulti\b/.test(key)) return <FiGrid className={optClass} />;
  if (/custom interface/.test(key)) return <FiSettings className={optClass} />;
  if (/direct interface/.test(key)) return <FiSquare className={optClass} />;
  if (/biohorizon|tri matrix|implant system/.test(key))
    return <TbDental className={optClass} />;
  if (/scan body|scan flag/.test(key)) return <FiFlag className={optClass} />;
  if (/platform|connection/.test(key)) return <FiLink className={optClass} />;
  if (/implant size|implant manufacturer/.test(key))
    return <FiSettings className={optClass} />;

  // Nightguard designs
  if (/michigan/.test(key)) return <FiLayers className={optClass} />;
  if (/bleaching tray/.test(key)) return <FiDroplet className={optClass} />;
  if (/simple nightguard|full coverage/.test(key))
    return <PiToothFill className={optClass} />;
  if (/flat/.test(key)) return <FiMinimize2 className={optClass} />;
  if (/follow occlusal|anatomy/.test(key)) return <FiMaximize2 className={optClass} />;

  // Smile / aesthetic
  if (/photo/.test(key)) return <FiCamera className={optClass} />;
  if (/wax up|additive|restorative/.test(key)) return <BiPalette className={optClass} />;
  if (/shade/.test(key)) return <BiPalette className={optClass} />;
  if (/smile|treatment plan/.test(key)) return <GiIncisors className={optClass} />;

  // Digital models
  if (/solid/.test(key)) return <BiCube className={optClass} />;
  if (/hollow/.test(key)) return <FiBox className={optClass} />;
  if (/dicom/.test(key)) return <FiLayers className={optClass} />;
  if (/stl/.test(key)) return <FiBox className={optClass} />;
  if (/die|sectioned|unsectioned|ditched/.test(key))
    return <FiScissors className={optClass} />;
  if (/articulator/.test(key)) return <FiTarget className={optClass} />;
  if (/quadrant/.test(key)) return <FiGrid className={optClass} />;
  if (/palate|vertex|full arch/.test(key)) return <GiJawbone className={optClass} />;
  if (/model|digital/.test(key)) return <BiCube className={optClass} />;

  // Surgical
  if (/pilot-drill|pilot/.test(key)) return <FiTarget className={optClass} />;
  if (/fully guided/.test(key)) return <TbDental className={optClass} />;
  if (/extraction|extract/.test(key)) return <FiScissors className={optClass} />;

  // Denture / RPD
  if (/monoblock/.test(key)) return <BiCube className={optClass} />;
  if (/separate gingiva|gingiva and teeth/.test(key))
    return <BiLayer className={optClass} />;
  if (/anatomic/.test(key) && /non/.test(key)) return <FiCircle className={optClass} />;
  if (/semi-anatomic/.test(key)) return <FiLayers className={optClass} />;
  if (/anatomic/.test(key)) return <GiIncisors className={optClass} />;
  if (/clasp|i bar|embrasure/.test(key)) return <TbBraces className={optClass} />;
  if (/framework|connector|retainer|rest\b|arch/.test(key))
    return <GiJawbone className={optClass} />;
  if (/denture/.test(key)) return <FaTooth className={optClass} />;

  // Tissue / prep
  if (/no preparation/.test(key)) return <RiToothLine className={optClass} />;
  if (/minimal preparation|prep/.test(key)) return <GiToothbrush className={optClass} />;
  if (/ovate/.test(key)) return <FiCircle className={optClass} />;
  if (/ridge lap|modified ridge/.test(key)) return <FiLayers className={optClass} />;
  if (/hygienic|conical|saddle/.test(key)) return <FiHexagon className={optClass} />;
  if (/gingiva|tissue|margin/.test(key)) return <GiToothbrush className={optClass} />;

  // Don't default to tooth — caller shows letter badge instead
  return null;
}
